import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Calendar, MapPin, Phone, Mail, ExternalLink, Facebook, Instagram, Music2, Youtube, Ticket, X, Send, Users, User, LogIn, Menu, ChevronRight, Flame, Newspaper, Video, Camera, ShoppingBag, CreditCard, MessagesSquare, Settings } from "lucide-react";
import { api, setToken, clearToken, getToken, assetUrl } from "../lib/api";
import { FacebookEmbed, FacebookLikeButton } from "../components/FacebookEmbed";
import { ArnakAudioPlayer } from "../components/ArnakAudioPlayer";
import ArnakWelcomeBanner from "../components/ArnakWelcomeBanner";

// Préfixe récursivement toutes les URLs /uploads/... avec l'origine cross-domain (utile sur arnakclan.com).
const rewriteAssets = (obj) => {
  if (obj == null) return obj;
  if (typeof obj === "string") return assetUrl(obj);
  if (Array.isArray(obj)) return obj.map(rewriteAssets);
  if (typeof obj === "object") {
    const out = {};
    for (const k of Object.keys(obj)) {
      const v = obj[k];
      if (typeof v === "string" && (k.endsWith("_url") || k === "url" || k === "image_url" || k === "photo_url" || k === "logo_url" || k === "cover_url" || k === "thumbnail_url")) {
        out[k] = assetUrl(v);
      } else {
        out[k] = rewriteAssets(v);
      }
    }
    return out;
  }
  return obj;
};
import { toast } from "sonner";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso.replace(" ", "T"));
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
};
const fmtDateTime = (iso) => {
  if (!iso) return "";
  const d = new Date(iso.replace(" ", "T"));
  return d.toLocaleString("fr-FR", { day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" });
};
const fmtAr = (n) => (n == null ? null : new Intl.NumberFormat("fr-FR").format(n) + " Ar");

// Formate un grand nombre en notation compacte (1234 → "1,2K", 12500 → "12K", 1500000 → "1,5M")
const fmtCompact = (n) => {
  const v = Number(n);
  if (!Number.isFinite(v) || v <= 0) return "";
  if (v < 1000) return String(Math.round(v));
  if (v < 10000) return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 }).format(v / 1000) + "K";
  if (v < 1000000) return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(v / 1000) + "K";
  return new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 1 }).format(v / 1000000) + "M";
};

// Normalise les URLs vidéo en URL d'embed iframe-compatible.
// YouTube/Facebook/TikTok refusent l'iframe sur les URL "page" (watch?v=, /reel/...) → on convertit.
const toEmbedUrl = (raw) => {
  if (!raw) return "";
  const u = String(raw).trim();
  // YouTube — watch, youtu.be, shorts, déjà /embed
  const yt = u.match(/(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/|live\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?rel=0&modestbranding=1`;
  // Facebook reels / videos / posts
  if (/facebook\.com\/(reel\/|.+\/videos\/|.+\/posts\/|watch\/\?v=)/.test(u) || /fb\.watch\//.test(u)) {
    return `https://www.facebook.com/plugins/video.php?href=${encodeURIComponent(u)}&show_text=false&autoplay=false`;
  }
  // TikTok — page → embed
  const tk = u.match(/tiktok\.com\/@[^/]+\/video\/(\d+)/) || u.match(/tiktok\.com\/v\/(\d+)/);
  if (tk) return `https://www.tiktok.com/embed/v2/${tk[1]}`;
  // Instagram reels/posts
  const ig = u.match(/instagram\.com\/(?:reel|p|tv)\/([A-Za-z0-9_-]+)/);
  if (ig) return `https://www.instagram.com/p/${ig[1]}/embed`;
  // Déjà un embed ou autre lecteur → on laisse tel quel
  return u;
};

const ArnakPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingEvent, setBookingEvent] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [showAccount, setShowAccount] = useState(false);
  const [user, setUser] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    document.title = "ARNAK — Tournée officielle";
    api.get("/arnak/public").then(({ data }) => { setData(rewriteAssets(data)); setLoading(false); })
      .catch(() => { toast.error("Erreur de chargement"); setLoading(false); });
    if (getToken()) {
      api.get("/auth/me").then(({ data }) => setUser(data)).catch(() => clearToken());
    }
    // Track visit (anonyme — pas d'auth requise)
    api.post("/arnak/track-visit", { path: window.location.pathname }).catch(() => {});
  }, []);

  if (loading) return <div className="min-h-screen bg-black text-white flex items-center justify-center text-sm">Chargement…</div>;
  if (!data) return null;

  const { settings: s, members, events, gallery, videos, news, partners } = data;
  const pastEvents = data.past_events || [];
  const products = data.products || [];
  const shopStatus = s.shop_status || "soon"; // soon | live | hidden
  const isArnakDomain = typeof window !== "undefined" && /arnakclan\.com$/i.test(window.location.hostname);
  const chatUrl = isArnakDomain ? "/chat" : "/arnak/chat";
  const radoUrl = isArnakDomain ? "/rado-production" : "/arnak/rado-production";
  const adsUrl  = isArnakDomain ? "/annonces" : "/arnak/annonces";

  // Grouper les concerts à venir par tournée
  const upcomingEvents = events.filter((e) => new Date(e.event_date.replace(" ", "T")) >= new Date(Date.now() - 86400000 * 2));
  const eventsByTour = upcomingEvents.reduce((acc, e) => {
    const t = e.tour_name?.trim() || "Autres dates";
    (acc[t] = acc[t] || []).push(e);
    return acc;
  }, {});

  // Affiche prioritaire pour le hero : prochain concert > cover par défaut
  const heroImage = upcomingEvents[0]?.image_url || s.cover_url;
  const heroEventTitle = upcomingEvents[0]?.title;

  const onLogout = () => { clearToken(); setUser(null); setShowAccount(false); toast.success("Déconnecté"); };

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white overflow-x-hidden" data-testid="arnak-page">
      {/* Bandeau bienvenue (tous les 7 jours pour les users connectés) */}
      <ArnakWelcomeBanner user={user} settings={s} />      {/* TOP NAV */}
      <nav className="sticky top-0 z-40 bg-black/85 backdrop-blur-xl border-b border-yellow-400/15">
        <div className="max-w-7xl mx-auto px-4 sm:px-5 md:px-10 py-3 md:py-2 flex items-center justify-between gap-3">
          <div className="flex items-center gap-6">
            <a href="#hero" className="flex items-center gap-2" data-testid="nav-logo">
              {s.logo_url ? (
                <img src={s.logo_url} alt={s.name} className="h-36 sm:h-40 md:h-40 w-auto object-contain drop-shadow-[0_4px_15px_rgba(250,204,21,0.4)]" />
              ) : (
                <span className="font-black text-3xl" style={{ fontFamily: "'Anton', sans-serif" }}>{s.name}</span>
              )}
            </a>
            <ul className="hidden lg:flex items-center gap-3.5 xl:gap-4 text-[11px] uppercase tracking-widest font-bold text-white/70">
              <li><a href="#a-propos" className="whitespace-nowrap hover:text-yellow-400 transition">À propos</a></li>
              <li><a href="#tournee" className="whitespace-nowrap hover:text-yellow-400 transition">Tournée</a></li>
              <li className="hidden xl:block"><a href="#archives" className="whitespace-nowrap hover:text-yellow-400 transition">Archives</a></li>
              <li className="hidden xl:block"><a href="#streaming" className="whitespace-nowrap hover:text-yellow-400 transition">Streaming</a></li>
              <li><a href="#clips" className="whitespace-nowrap hover:text-yellow-400 transition">Clips</a></li>
              {videos.some((v) => v.media_type === "audio") && (
                <li><a href="#sons" className="whitespace-nowrap hover:text-yellow-400 transition">Sons</a></li>
              )}
              <li className="hidden xl:block"><a href="#crew" className="whitespace-nowrap hover:text-yellow-400 transition">Crew</a></li>
              {shopStatus !== "hidden" && <li><a href="#boutique" className="whitespace-nowrap hover:text-yellow-400 transition">Boutique</a></li>}
              <li>
                <Link to={adsUrl} className="whitespace-nowrap hover:text-yellow-400 transition" data-testid="nav-ads-link">
                  Annonces
                </Link>
              </li>
              {s.rado_active !== "0" && (
                <li>
                  <Link to={radoUrl} className="whitespace-nowrap hover:text-yellow-400 transition" data-testid="nav-rado-link">
                    RADO
                  </Link>
                </li>
              )}
              <li><a href={chatUrl} className="whitespace-nowrap hover:text-yellow-400 transition">Chat</a></li>
              <li className="hidden xl:block"><a href="#contact" className="whitespace-nowrap hover:text-yellow-400 transition">Contact</a></li>
            </ul>
          </div>
          <div className="flex items-center gap-2 md:gap-2.5">
            {/* Compteur d'abonnés Facebook (desktop md+) */}
            {s.facebook_followers_count && fmtCompact(s.facebook_followers_count) && (
              <a
                href={s.facebook_page_url || s.fb_url || "#"}
                target="_blank"
                rel="noreferrer"
                className="hidden md:inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#1877F2]/15 hover:bg-[#1877F2]/25 border border-[#1877F2]/40 hover:border-[#1877F2]/70 text-white transition group"
                title="Suivre ARNAK sur Facebook"
                data-testid="nav-fb-followers"
              >
                <Facebook size={14} className="text-[#4d92ff] group-hover:text-white transition" fill="currentColor" />
                <span className="text-[11px] font-black uppercase tracking-wider leading-none">
                  {fmtCompact(s.facebook_followers_count)}
                  <span className="ml-1 text-white/70 font-bold normal-case tracking-normal">abonnés</span>
                </span>
              </a>
            )}
            <div className="hidden xl:flex items-center gap-1.5">
              {s.fb_url && <a href={s.fb_url} target="_blank" rel="noreferrer" className="p-2 rounded-full hover:bg-yellow-400/15 hover:text-yellow-400 transition text-white/80" aria-label="Facebook"><Facebook size={16} /></a>}
              {s.ig_url && <a href={s.ig_url} target="_blank" rel="noreferrer" className="p-2 rounded-full hover:bg-yellow-400/15 hover:text-yellow-400 transition text-white/80" aria-label="Instagram"><Instagram size={16} /></a>}
              {s.tt_url && <a href={s.tt_url} target="_blank" rel="noreferrer" className="p-2 rounded-full hover:bg-yellow-400/15 hover:text-yellow-400 transition text-white/80" aria-label="TikTok"><Music2 size={16} /></a>}
              {s.yt_url && <a href={s.yt_url} target="_blank" rel="noreferrer" className="p-2 rounded-full hover:bg-yellow-400/15 hover:text-yellow-400 transition text-white/80" aria-label="YouTube"><Youtube size={16} /></a>}
            </div>
            {user ? (
              <button onClick={() => setShowAccount(true)} className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 hover:bg-white/10 text-sm font-bold whitespace-nowrap transition" data-testid="account-btn">
                <User size={14} /> {user.name?.split(" ")[0] || "Compte"}
              </button>
            ) : (
              <button onClick={() => setShowLogin(true)} className="hidden md:inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/5 hover:bg-white/10 text-sm font-bold whitespace-nowrap transition" data-testid="login-btn">
                <LogIn size={14} /> Connexion
              </button>
            )}
            {/* Bouton "Panel ARNAK" visible UNIQUEMENT pour les admins ARNAK (full ou restreints, et hors no_arnak) */}
            {user && ((user.is_admin && !user.no_arnak) || user.is_arnak_admin) && (
              <Link
                to="/admin"
                className="hidden md:inline-flex items-center gap-2 px-3.5 py-2.5 rounded-full bg-yellow-400/20 hover:bg-yellow-400/35 border border-yellow-400/40 text-yellow-300 hover:text-yellow-200 text-xs font-black uppercase tracking-widest transition"
                title="Panel d'administration ARNAK"
                data-testid="nav-admin-arnak"
              >
                <Settings size={13} /> Admin
              </Link>
            )}
            <a href="#tournee" className="inline-flex items-center gap-2 px-4 md:px-5 py-2.5 md:py-2.5 bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-widest text-xs md:text-sm transition" data-testid="nav-billetterie">
              <Ticket size={16} className="md:hidden" /><Ticket size={14} className="hidden md:inline" /> <span className="hidden sm:inline">Billetterie</span><span className="sm:hidden">Billets</span>
            </a>
            {/* Hamburger mobile (visible < lg) */}
            <button
              onClick={() => setMenuOpen(true)}
              className="lg:hidden p-2.5 -mr-1 rounded-lg text-white hover:text-yellow-400 hover:bg-white/5 transition"
              aria-label="Ouvrir le menu"
              data-testid="mobile-menu-toggle"
            >
              <Menu size={34} strokeWidth={2.5} />
            </button>
          </div>
        </div>
      </nav>

      {/* DRAWER MOBILE (hamburger) */}
      {menuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-drawer" onClick={() => setMenuOpen(false)}>
          <div className="absolute inset-0 bg-black/90 backdrop-blur-md" />
          <aside
            onClick={(e) => e.stopPropagation()}
            className="absolute top-0 right-0 h-full w-[88%] max-w-md bg-zinc-950 border-l-2 border-yellow-400/40 shadow-[-12px_0_60px_rgba(250,204,21,0.18)] flex flex-col"
          >
            <div className="flex items-center justify-between px-5 py-5 border-b border-yellow-400/20">
              {s.logo_url ? (
                <img src={s.logo_url} alt={s.name} className="h-24 w-auto object-contain" />
              ) : (
                <span className="font-black text-3xl" style={{ fontFamily: "'Anton', sans-serif" }}>{s.name}</span>
              )}
              <button
                onClick={() => setMenuOpen(false)}
                className="p-3 rounded-full hover:bg-white/10 text-white hover:text-yellow-400 transition"
                aria-label="Fermer"
                data-testid="mobile-menu-close"
              >
                <X size={32} strokeWidth={2.5} />
              </button>
            </div>

            <nav className="flex-1 overflow-y-auto py-3 px-3">
              <ul className="space-y-1">
                {[
                  { href: "#hero",      label: "Accueil",            icon: Flame },
                  { href: "#a-propos",  label: "À propos",           icon: Users },
                  { href: "#tournee",   label: "Tournée",            icon: Ticket },
                  { href: "#archives",  label: "Archives",           icon: Calendar },
                  { href: "#streaming", label: "Streaming",          icon: Music2 },
                  { href: "#actus",     label: "Actualités",         icon: Newspaper },
                  { href: "#clips",     label: "Clips",              icon: Video },
                  ...(videos.some((v) => v.media_type === "audio") ? [{ href: "#sons", label: "🎧 Sons", icon: Music2 }] : []),
                  { href: "#crew",      label: "Le collectif",       icon: User },
                  { href: "#galerie",   label: "Galerie",            icon: Camera },
                  ...(shopStatus !== "hidden" ? [{ href: "#boutique",  label: "Boutique",   icon: ShoppingBag }] : []),
                  { href: adsUrl, label: "🛒 Annonces", icon: ShoppingBag, isRoute: true },
                  ...(s.rado_active !== "0" ? [{ href: radoUrl,   label: "🎙️ RADO Production",  icon: Music2, isRoute: true }] : []),
                  { href: chatUrl,      label: "💬 ArnakChat",       icon: MessagesSquare },
                  { href: "#contact",   label: "Contact / Booking",  icon: Mail },
                ].map((l) => {
                  const Ic = l.icon;
                  return (
                    <li key={l.href}>
                      {l.isRoute ? (
                        <Link
                          to={l.href}
                          onClick={() => setMenuOpen(false)}
                          className="flex items-center gap-4 px-4 py-5 rounded-lg text-white hover:text-yellow-400 hover:bg-yellow-400/10 active:bg-yellow-400/20 transition font-black uppercase tracking-wider text-xl"
                          data-testid={`mobile-link-${l.href.replace(/[^a-z0-9]/g, "-")}`}
                        >
                          <Ic size={26} strokeWidth={2.2} className="text-yellow-400 shrink-0" />
                          {l.label}
                        </Link>
                      ) : (
                        <a
                          href={l.href}
                          onClick={() => setMenuOpen(false)}
                          className="flex items-center gap-4 px-4 py-5 rounded-lg text-white hover:text-yellow-400 hover:bg-yellow-400/10 active:bg-yellow-400/20 transition font-black uppercase tracking-wider text-xl"
                          data-testid={`mobile-link-${l.href.replace("#", "")}`}
                        >
                          <Ic size={26} strokeWidth={2.2} className="text-yellow-400 shrink-0" />
                          {l.label}
                        </a>
                      )}
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Réseaux + compte en bas du drawer */}
            <div className="px-5 py-5 border-t border-yellow-400/20 space-y-4">
              {/* Bandeau abonnés Facebook (mobile) */}
              {s.facebook_followers_count && fmtCompact(s.facebook_followers_count) && (
                <a
                  href={s.facebook_page_url || s.fb_url || "#"}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() => setMenuOpen(false)}
                  className="flex items-center justify-center gap-2.5 w-full px-4 py-3 rounded-lg bg-[#1877F2]/15 hover:bg-[#1877F2]/30 border border-[#1877F2]/40 text-white transition"
                  data-testid="mobile-fb-followers"
                >
                  <Facebook size={18} className="text-[#4d92ff]" fill="currentColor" />
                  <span className="text-sm font-black uppercase tracking-wider">
                    {fmtCompact(s.facebook_followers_count)}
                    <span className="ml-1.5 text-white/75 font-bold normal-case tracking-normal">abonnés Facebook</span>
                  </span>
                </a>
              )}
              {(s.fb_url || s.ig_url || s.tt_url || s.yt_url) && (
                <div className="flex items-center justify-center gap-3">
                  {s.fb_url && <a href={s.fb_url} target="_blank" rel="noreferrer" className="p-3.5 rounded-full bg-white/5 hover:bg-yellow-400/20 hover:text-yellow-400 text-white transition" aria-label="Facebook"><Facebook size={24} /></a>}
                  {s.ig_url && <a href={s.ig_url} target="_blank" rel="noreferrer" className="p-3.5 rounded-full bg-white/5 hover:bg-yellow-400/20 hover:text-yellow-400 text-white transition" aria-label="Instagram"><Instagram size={24} /></a>}
                  {s.tt_url && <a href={s.tt_url} target="_blank" rel="noreferrer" className="p-3.5 rounded-full bg-white/5 hover:bg-yellow-400/20 hover:text-yellow-400 text-white transition" aria-label="TikTok"><Music2 size={24} /></a>}
                  {s.yt_url && <a href={s.yt_url} target="_blank" rel="noreferrer" className="p-3.5 rounded-full bg-white/5 hover:bg-yellow-400/20 hover:text-yellow-400 text-white transition" aria-label="YouTube"><Youtube size={24} /></a>}
                </div>
              )}
              {user ? (
                <button
                  onClick={() => { setShowAccount(true); setMenuOpen(false); }}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-4 rounded-lg bg-white/10 hover:bg-white/20 text-base font-black uppercase tracking-widest transition"
                  data-testid="mobile-account-btn"
                >
                  <User size={18} /> {user.name?.split(" ")[0] || "Mon compte"}
                </button>
              ) : (
                <button
                  onClick={() => { setShowLogin(true); setMenuOpen(false); }}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-4 rounded-lg bg-yellow-400 hover:bg-yellow-300 text-black text-base font-black uppercase tracking-widest transition"
                  data-testid="mobile-login-btn"
                >
                  <LogIn size={18} /> Se connecter
                </button>
              )}
              {user && ((user.is_admin && !user.no_arnak) || user.is_arnak_admin) && (
                <Link
                  to="/admin"
                  onClick={() => setMenuOpen(false)}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-4 rounded-lg bg-yellow-400/15 hover:bg-yellow-400/30 border border-yellow-400/40 text-yellow-300 text-base font-black uppercase tracking-widest transition"
                  data-testid="mobile-admin-arnak-btn"
                >
                  <Settings size={18} /> Panel ARNAK
                </Link>
              )}
            </div>
          </aside>
        </div>
      )}

      {/* HERO */}
      <header id="hero" className="relative min-h-[100vh] flex flex-col bg-black">
        {/* Affiche complète, jamais coupée — fond noir flouté pour habiller les marges */}
        <div className="absolute inset-0 overflow-hidden">
          {heroImage ? (
            <>
              <img src={heroImage} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover scale-110 blur-3xl opacity-50" />
              <img src={heroImage} alt="" className="relative w-full h-full object-contain" data-testid="hero-poster" />
            </>
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-yellow-400/20 via-blue-600/10 to-black" />
          )}
          {/* Vignette discrète bas seulement (pour lisibilité boutons), zéro overlay sur les visages */}
          <div className="absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-black via-black/70 to-transparent" />
        </div>
        <div className="relative z-10 flex-1 flex flex-col px-6 md:px-12 pb-8 md:pb-12 max-w-6xl mx-auto w-full">
          {/* L'affiche prend toute la hauteur ; le bloc CTA reste compact en bas */}
          <div className="flex-1" />
          <div className="space-y-2 md:space-y-3">
            {heroEventTitle && (
              <p className="inline-flex items-center gap-2 text-yellow-400 text-[10px] md:text-xs uppercase tracking-[0.35em] font-black bg-black/75 px-3 py-1.5 backdrop-blur w-fit">
                ★ Prochain concert
              </p>
            )}
            {s.tagline && <p className="text-yellow-400 text-xs md:text-base uppercase tracking-[0.35em] font-bold">{s.tagline}</p>}
            <h1 className="font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[0.9] tracking-tighter text-yellow-400" style={{ fontFamily: "'Anton', sans-serif", textShadow: "0 4px 30px rgba(0,0,0,0.95), 0 0 60px rgba(250, 204, 21, 0.3)" }} data-testid="arnak-title">
              EN TOURNÉE
            </h1>
            {heroEventTitle && (
              <p className="text-white text-lg md:text-2xl font-black tracking-tight" style={{ textShadow: "0 2px 12px rgba(0,0,0,0.95)" }} data-testid="hero-event-title">
                {heroEventTitle}
              </p>
            )}
            {upcomingEvents.length > 0 && (
              <div className="flex flex-wrap gap-3 pt-1">
                <button onClick={() => document.getElementById("tournee")?.scrollIntoView({ behavior: "smooth" })}
                  className="inline-flex items-center gap-2 px-6 md:px-7 py-3.5 md:py-4 bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-widest text-xs md:text-sm transition shadow-[0_8px_30px_rgba(250,204,21,0.4)]" data-testid="cta-tour">
                  <Ticket size={14} /> Voir les dates
                </button>
                <button onClick={() => setBookingEvent(upcomingEvents[0])}
                  className="inline-flex items-center gap-2 px-6 md:px-7 py-3.5 md:py-4 bg-white/15 hover:bg-white/25 border border-white/30 backdrop-blur text-white font-black uppercase tracking-widest text-xs md:text-sm transition" data-testid="cta-next">
                  Réserver <ChevronRight size={13} />
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* À PROPOS */}
      <section id="a-propos" className="px-6 md:px-12 py-16 md:py-24 max-w-5xl mx-auto">
        <p className="text-yellow-400 text-[10px] uppercase tracking-[0.4em] font-bold mb-3">À propos</p>
        <h2 className="font-black text-4xl md:text-6xl tracking-tighter mb-6" style={{ fontFamily: "'Anton', sans-serif" }}>LE COLLECTIF</h2>
        <p className="text-base md:text-xl text-white/75 leading-relaxed max-w-3xl">{s.bio}</p>
        <div className="flex flex-wrap items-center gap-4 mt-6 text-[11px] uppercase tracking-widest font-bold text-white/55">
          {s.genre && <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-yellow-400/15 text-yellow-300 border border-yellow-400/30"><Flame size={11} /> {s.genre}</span>}
          {s.city && <span className="inline-flex items-center gap-1.5"><MapPin size={11} /> {s.city}{s.country ? `, ${s.country}` : ""}</span>}
          {members.length > 0 && <span className="inline-flex items-center gap-1.5"><Users size={11} /> {members.length} membres</span>}
        </div>
      </section>

      {/* TOURNÉE GROUPÉE PAR TOUR */}
      {upcomingEvents.length > 0 && (
        <section id="tournee" className="px-6 md:px-12 py-16 md:py-24 bg-white/[0.02] border-y border-white/5">
          <div className="max-w-7xl mx-auto">
            <p className="text-yellow-400 text-[10px] uppercase tracking-[0.4em] font-bold mb-3">Billetterie</p>
            <h2 className="font-black text-4xl md:text-6xl tracking-tighter mb-3" style={{ fontFamily: "'Anton', sans-serif" }}>DATES À VENIR</h2>
            <p className="text-white/55 text-sm md:text-base mb-10">{upcomingEvents.length} concert{upcomingEvents.length > 1 ? "s" : ""} programmé{upcomingEvents.length > 1 ? "s" : ""} · Réserve ta place dès maintenant.</p>

            {/* FEATURED — Prochain concert mis en avant */}
            <FeaturedEvent event={upcomingEvents[0]} onBook={() => setBookingEvent(upcomingEvents[0])} stripeUrl={s.stripe_payment_url} />

            <div className="space-y-10 mt-12">
              {Object.entries(eventsByTour).map(([tourName, list]) => (
                <div key={tourName}>
                  <div className="flex items-end justify-between mb-4 flex-wrap gap-3 pb-3 border-b border-yellow-400/30">
                    <h3 className="font-black text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "'Anton', sans-serif" }}>{tourName.toUpperCase()}</h3>
                    <span className="text-yellow-400 text-[10px] uppercase tracking-widest font-bold">{list.length} date{list.length > 1 ? "s" : ""}</span>
                  </div>
                  <div className="space-y-2">
                    {list.map((e) => <TourDateRow key={e.event_id} event={e} onBook={() => setBookingEvent(e)} />)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* VOUS AVEZ LOUPÉ — Concerts passés */}
      {pastEvents.length > 0 && (
        <section id="archives" className="px-6 md:px-12 py-16 md:py-24">
          <div className="max-w-7xl mx-auto">
            <p className="text-orange-400 text-[10px] uppercase tracking-[0.4em] font-bold mb-3">Archives</p>
            <h2 className="font-black text-4xl md:text-6xl tracking-tighter mb-3" style={{ fontFamily: "'Anton', sans-serif" }}>VOUS AVEZ LOUPÉ</h2>
            <p className="text-white/55 text-sm md:text-base mb-10">Revivez les concerts passés en photos et en vidéos.</p>
            <div className="space-y-12">
              {pastEvents.map((e) => <PastEventCard key={e.event_id} event={e} />)}
            </div>
          </div>
        </section>
      )}

      {/* ACTUALITÉS + FLUX FACEBOOK */}
      {(news.length > 0 || s.facebook_page_url) && (
        <section id="actus" className="px-6 md:px-12 py-16 md:py-24 max-w-7xl mx-auto">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-10">
            <div>
              <p className="text-yellow-400 text-[10px] uppercase tracking-[0.4em] font-bold mb-3">News</p>
              <h2 className="font-black text-4xl md:text-6xl tracking-tighter" style={{ fontFamily: "'Anton', sans-serif" }}>LES ACTUALITÉS</h2>
            </div>
            {s.facebook_page_url && (
              <FacebookLikeButton url={s.facebook_page_url} label="Suivre ARNAK sur Facebook" />
            )}
          </div>

          <div className={`grid gap-8 ${s.facebook_page_url ? "lg:grid-cols-[420px_1fr]" : ""}`}>
            {s.facebook_page_url && (
              <aside className="space-y-3 order-first">
                <p className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold inline-flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-[#1877F2] rounded-full animate-pulse" /> Direct Facebook
                </p>
                <div className="bg-white/[0.03] border border-white/10 rounded-xl overflow-hidden p-2">
                  <FacebookEmbed url={s.facebook_page_url} height={700} />
                </div>
              </aside>
            )}
            <div>
              {news.length > 0 ? (
                <>
                  <p className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-3">Sur le site</p>
                  <div className="grid sm:grid-cols-2 gap-5">
                    {news.map((n) => <NewsCard key={n.news_id} item={n} />)}
                  </div>
                </>
              ) : (
                <p className="text-white/55 text-sm">Les dernières publications du collectif s'affichent ci-contre directement depuis la page Facebook ARNAK 👈</p>
              )}
            </div>
          </div>
        </section>
      )}

      {/* BIENTÔT SUR LES PLATEFORMES */}
      <section id="streaming" className="px-6 md:px-12 py-16 md:py-24 bg-gradient-to-b from-black via-yellow-400/[0.04] to-black border-y border-yellow-400/20">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10 md:mb-12">
            <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-yellow-400 text-black text-[10px] uppercase tracking-[0.35em] font-black mb-5 animate-pulse">
              ★ Bientôt disponible
            </span>
            <h2 className="font-black text-4xl md:text-6xl tracking-tighter mb-3" style={{ fontFamily: "'Anton', sans-serif" }}>
              ÉCOUTE LE SON D'<span className="text-yellow-400">ARNAK</span>
            </h2>
            <p className="text-white/65 text-sm md:text-base max-w-2xl mx-auto">
              Les morceaux du collectif arrivent très prochainement sur toutes les grandes plateformes de streaming. Reste connecté.
            </p>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3 md:gap-4">
            {[
              { name: "Spotify",       slug: "spotify" },
              { name: "Apple Music",   slug: "applemusic" },
              { name: "Deezer",        slug: "deezer" },
              { name: "YouTube Music", slug: "youtubemusic" },
              { name: "Amazon Music",  slug: "amazonmusic" },
              { name: "TikTok",        slug: "tiktok" },
            ].map((p) => {
              const maskUrl = `url(https://cdn.jsdelivr.net/npm/simple-icons@latest/icons/${p.slug}.svg)`;
              return (
                <div key={p.slug} className="group relative aspect-square flex flex-col items-center justify-center gap-2.5 p-4 bg-white/[0.03] border border-white/10 hover:border-yellow-400/70 hover:bg-yellow-400/5 transition" data-testid={`platform-${p.slug}`}>
                  <div
                    aria-label={p.name}
                    className="w-10 h-10 md:w-12 md:h-12 bg-yellow-400 group-hover:bg-yellow-300 group-hover:scale-110 transition duration-300"
                    style={{
                      WebkitMaskImage: maskUrl, maskImage: maskUrl,
                      WebkitMaskSize: "contain", maskSize: "contain",
                      WebkitMaskRepeat: "no-repeat", maskRepeat: "no-repeat",
                      WebkitMaskPosition: "center", maskPosition: "center",
                    }}
                  />
                  <p className="text-[10px] md:text-xs uppercase tracking-widest font-bold text-white/70 group-hover:text-yellow-300 text-center leading-tight">{p.name}</p>
                  <span className="absolute top-1.5 right-1.5 text-[8px] uppercase tracking-widest font-bold text-yellow-400/70">Soon</span>
                </div>
              );
            })}
          </div>

          <div className="mt-8 md:mt-10 text-center">
            <p className="text-[11px] uppercase tracking-[0.3em] text-white/45 font-bold">
              Notifications de sortie · Abonne-toi à la newsletter ci-dessous
            </p>
          </div>
        </div>
      </section>

      {/* CLIPS (vidéos uniquement) */}
      {videos.filter((v) => v.media_type !== "audio").length > 0 && (
        <section id="clips" className="px-6 md:px-12 py-16 md:py-24 bg-white/[0.02] border-y border-white/5">
          <div className="max-w-7xl mx-auto">
            <p className="text-yellow-400 text-[10px] uppercase tracking-[0.4em] font-bold mb-3">Vidéos</p>
            <h2 className="font-black text-4xl md:text-6xl tracking-tighter mb-10" style={{ fontFamily: "'Anton', sans-serif" }}>DERNIERS CLIPS</h2>
            <div className="grid md:grid-cols-2 gap-5">
              {videos.filter((v) => v.media_type !== "audio").map((v) => (
                <div key={v.video_id} className="border border-white/10 hover:border-yellow-400/50 transition" data-testid={`video-${v.video_id}`}>
                  <div className="aspect-video bg-black">
                    <iframe
                      src={toEmbedUrl(v.embed_url)}
                      title={v.title}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      referrerPolicy="strict-origin-when-cross-origin"
                      loading="lazy"
                      allowFullScreen
                    />
                  </div>
                  <p className="p-3 font-bold text-sm">{v.title}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SONS (audios uniquement) */}
      {videos.filter((v) => v.media_type === "audio").length > 0 && (
        <section id="sons" className="px-6 md:px-12 py-16 md:py-24" data-testid="sons-section">
          <div className="max-w-5xl mx-auto">
            <p className="text-yellow-400 text-[10px] uppercase tracking-[0.4em] font-bold mb-3">Audio</p>
            <h2 className="font-black text-4xl md:text-6xl tracking-tighter mb-3" style={{ fontFamily: "'Anton', sans-serif" }}>SONS ARNAK</h2>
            <p className="text-white/55 text-sm md:text-base mb-10 max-w-2xl">
              Beats, prods, extraits et freestyles. Écoute en direct sans quitter la page.
            </p>
            <div className="grid sm:grid-cols-2 gap-5">
              {videos.filter((v) => v.media_type === "audio").map((v) => (
                <ArnakAudioPlayer key={v.video_id} item={v} fallbackThumb={s.logo_url} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* MEMBRES */}
      {members.length > 0 && (
        <section id="crew" className="px-6 md:px-12 py-16 md:py-24 max-w-7xl mx-auto">
          <p className="text-yellow-400 text-[10px] uppercase tracking-[0.4em] font-bold mb-3">Crew</p>
          <h2 className="font-black text-4xl md:text-6xl tracking-tighter mb-10" style={{ fontFamily: "'Anton', sans-serif" }}>LE COLLECTIF</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {members.map((m) => <MemberCard key={m.member_id} member={m} />)}
          </div>
        </section>
      )}

      {/* GALERIE */}
      {gallery.length > 0 && (
        <section id="galerie" className="px-6 md:px-12 py-16 md:py-24 max-w-7xl mx-auto">
          <h2 className="font-black text-4xl md:text-6xl tracking-tighter mb-10" style={{ fontFamily: "'Anton', sans-serif" }}>GALERIE</h2>
          <GalleryPublic photos={gallery} />
        </section>
      )}

      {/* BOUTIQUE */}
      {shopStatus !== "hidden" && (
        <section id="boutique" className="px-6 md:px-12 py-16 md:py-24 max-w-7xl mx-auto">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-10">
            <div>
              <p className="text-yellow-400 text-[10px] uppercase tracking-[0.4em] font-bold mb-3">Merch</p>
              <h2 className="font-black text-4xl md:text-6xl tracking-tighter" style={{ fontFamily: "'Anton', sans-serif" }}>
                LA BOUTIQUE
              </h2>
              <p className="text-white/55 text-sm md:text-base mt-2">T-shirts, casquettes et accessoires officiels ARNAK.</p>
            </div>
            {shopStatus === "soon" && (
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-yellow-400 text-black text-[10px] uppercase tracking-[0.35em] font-black animate-pulse self-start">
                ★ Bientôt disponible
              </span>
            )}
          </div>

          {products.length === 0 ? (
            <div className="border border-dashed border-yellow-400/30 bg-yellow-400/[0.03] rounded-xl overflow-hidden">
              {s.shop_placeholder_image && (
                <div className="relative aspect-[16/9] sm:aspect-[21/9] bg-black overflow-hidden">
                  <img src={s.shop_placeholder_image} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-40" />
                  <img src={s.shop_placeholder_image} alt="Boutique ARNAK — Bientôt disponible" className="relative w-full h-full object-contain" data-testid="shop-placeholder-image" />
                </div>
              )}
              <div className="py-12 px-6 text-center">
                {!s.shop_placeholder_image && <ShoppingBag size={40} className="text-yellow-400/60 mx-auto mb-4" />}
                <p className="text-lg md:text-xl font-bold mb-2">La boutique arrive très prochainement</p>
                <p className="text-sm text-white/55 max-w-md mx-auto">Inscris-toi à la newsletter ci-dessous pour être averti·e dès le lancement.</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {products.map((p) => <ProductCard key={p.product_id} product={p} status={shopStatus} stripeUrl={s.stripe_payment_url} />)}
            </div>
          )}
        </section>
      )}

      {/* NEWSLETTER */}
      <NewsletterBlock user={user} />

      {/* PARTENAIRES */}
      {(partners.length > 0 || (s.rado_active !== "0" && (s.rado_name || s.rado_fb_url))) && (
        <section className="px-6 md:px-12 py-12 border-t border-white/5">
          <div className="max-w-6xl mx-auto">
            <p className="text-center text-[10px] uppercase tracking-[0.4em] font-bold text-white/40 mb-6">Partenaires</p>

            {/* Carte RADO Production en avant */}
            {s.rado_active !== "0" && (s.rado_name || s.rado_fb_url) && (
              <Link
                to={radoUrl}
                className="block mb-8 group"
                data-testid="partners-rado-card"
              >
                <div className="bg-gradient-to-br from-white/[0.03] to-white/[0.01] border border-white/10 hover:border-yellow-400/40 rounded-2xl p-5 md:p-6 transition flex flex-col md:flex-row items-center gap-5 md:gap-7">
                  {s.rado_logo_url && (
                    <img
                      src={s.rado_logo_url}
                      alt={s.rado_name || "RADO Production"}
                      className="h-20 md:h-24 w-auto object-contain shrink-0"
                    />
                  )}
                  <div className="flex-1 min-w-0 text-center md:text-left">
                    <p className="text-[10px] uppercase tracking-[0.4em] text-yellow-400/80 font-bold mb-1.5">Studio partenaire</p>
                    <h3
                      className="font-black text-2xl md:text-3xl tracking-tight mb-1.5 group-hover:text-yellow-400 transition"
                      style={{ fontFamily: "'Anton', sans-serif" }}
                    >
                      {s.rado_name || "RADO Production"}
                    </h3>
                    <p className="text-white/65 text-sm">
                      {s.rado_tagline || "Studio de production · Toamasina"}
                    </p>
                  </div>
                  <span className="text-yellow-400 text-xs uppercase tracking-widest font-black inline-flex items-center gap-1.5 group-hover:translate-x-1 transition">
                    Découvrir <ChevronRight size={14} />
                  </span>
                </div>
              </Link>
            )}

            {partners.length > 0 && (
              <div className="flex items-center justify-center flex-wrap gap-8 opacity-60 hover:opacity-100 transition">
                {partners.map((p) => (
                  p.logo_url ? (
                    <a key={p.partner_id} href={p.link_url || "#"} target="_blank" rel="noreferrer" className="block">
                      <img src={p.logo_url} alt={p.name} className="h-10 md:h-12 max-w-[140px] object-contain" />
                    </a>
                  ) : (
                    <span key={p.partner_id} className="text-white/55 text-xs uppercase tracking-widest font-bold">{p.name}</span>
                  )
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* CONTACT / BOOKING */}
      <section id="contact" className="px-6 md:px-12 py-16 md:py-24 bg-yellow-400 text-black">
        <div className="max-w-5xl mx-auto grid md:grid-cols-[2fr_1fr] gap-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.4em] font-black mb-3">Booking & Management</p>
            <h2 className="font-black text-4xl md:text-6xl tracking-tighter leading-[0.9] mb-6" style={{ fontFamily: "'Anton', sans-serif" }}>UN ÉVÉNEMENT ?<br />ON VIENT.</h2>
            <p className="text-base md:text-lg max-w-xl">Festival, événement privé, plateau TV, marque : contactez l'équipe pour un devis personnalisé.</p>
          </div>
          <div className="space-y-3 text-sm">
            {s.booking_email && <a href={`mailto:${s.booking_email}`} className="flex bg-black text-yellow-400 px-5 py-4 font-bold items-center gap-3 hover:bg-zinc-900 transition" data-testid="booking-email"><Mail size={14} /> {s.booking_email}</a>}
            {s.booking_phone && <a href={`tel:${s.booking_phone}`} className="flex bg-black text-yellow-400 px-5 py-4 font-bold items-center gap-3 hover:bg-zinc-900 transition" data-testid="booking-phone"><Phone size={14} /> {s.booking_phone}</a>}
            {s.whatsapp && <a href={`https://wa.me/${s.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer" className="flex bg-black text-yellow-400 px-5 py-4 font-bold items-center gap-3 hover:bg-zinc-900 transition" data-testid="booking-whatsapp"><Send size={14} /> WhatsApp</a>}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="px-6 md:px-12 py-10 text-center text-[10px] uppercase tracking-[0.3em] text-white/30 space-y-3">
        <div className="flex items-center justify-center gap-3">
          {s.fb_url && <a href={s.fb_url} target="_blank" rel="noreferrer" className="hover:text-yellow-400 transition"><Facebook size={14} /></a>}
          {s.ig_url && <a href={s.ig_url} target="_blank" rel="noreferrer" className="hover:text-yellow-400 transition"><Instagram size={14} /></a>}
          {s.tt_url && <a href={s.tt_url} target="_blank" rel="noreferrer" className="hover:text-yellow-400 transition"><Music2 size={14} /></a>}
          {s.yt_url && <a href={s.yt_url} target="_blank" rel="noreferrer" className="hover:text-yellow-400 transition"><Youtube size={14} /></a>}
        </div>
        {s.rado_active !== "0" && (
          <p>
            <Link to={radoUrl} className="hover:text-yellow-400 transition" data-testid="footer-rado-link">
              🎙️ {s.rado_name || "RADO Production"} — Studio partenaire
            </Link>
          </p>
        )}
        <p>© {new Date().getFullYear()} {s.name} · Tous droits réservés · Powered by <Link to="/" className="hover:text-yellow-400 transition">Voamada</Link></p>
      </footer>

      {bookingEvent && <BookingModal event={bookingEvent} onClose={() => setBookingEvent(null)} user={user} />}
      {showLogin && <LoginModal onClose={() => setShowLogin(false)} onSuccess={(u) => { setUser(u); setShowLogin(false); }} />}
      {showAccount && user && <AccountModal user={user} onClose={() => setShowAccount(false)} onLogout={onLogout} />}
    </div>
  );
};

// ─── FEATURED EVENT (Prochain concert mis en avant) ───
const FeaturedEvent = ({ event: e, onBook, stripeUrl }) => {
  const [cd, setCd] = useState(diffParts(e.event_date));
  useEffect(() => {
    const t = setInterval(() => setCd(diffParts(e.event_date)), 1000);
    return () => clearInterval(t);
  }, [e.event_date]);
  const lat = parseFloat(e.venue_lat);
  const lng = parseFloat(e.venue_lng);
  const hasMap = !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0;
  const mapSrc = hasMap
    ? `https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.008}%2C${lat - 0.005}%2C${lng + 0.008}%2C${lat + 0.005}&layer=mapnik&marker=${lat}%2C${lng}`
    : null;

  return (
    <div className="relative overflow-hidden border-2 border-yellow-400/60 bg-gradient-to-br from-yellow-400/[0.06] to-transparent shadow-[0_0_40px_rgba(250,204,21,0.15)]" data-testid="featured-event">
      <div className="absolute top-3 left-3 z-10 px-3 py-1 bg-yellow-400 text-black text-[9px] uppercase tracking-[0.3em] font-black">★ À la une</div>
      <div className="grid md:grid-cols-[1fr_1.1fr] gap-0">
        {e.image_url && (
          <a href={e.image_url} target="_blank" rel="noreferrer" className="relative block bg-black aspect-[3/4] md:aspect-auto md:max-h-[600px] overflow-hidden">
            {/* Fond flouté pour habiller les marges, puis affiche complète centrée */}
            <img src={e.image_url} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-50" />
            <img src={e.image_url} alt={e.title} className="relative w-full h-full object-contain" data-testid="featured-poster" />
          </a>
        )}
        <div className="p-5 md:p-8 flex flex-col gap-4">
          {e.tour_name && <p className="text-yellow-400 text-[10px] uppercase tracking-[0.3em] font-black">{e.tour_name}</p>}
          <h3 className="font-black text-3xl md:text-5xl tracking-tighter leading-[0.9]" style={{ fontFamily: "'Anton', sans-serif" }}>{e.title}</h3>

          {/* Countdown */}
          <div className="grid grid-cols-4 gap-1.5 md:gap-2">
            <CdBox value={cd.days}   label="Jours" />
            <CdBox value={cd.hours}  label="Heures" />
            <CdBox value={cd.mins}   label="Min" />
            <CdBox value={cd.secs}   label="Sec" />
          </div>

          {/* Infos */}
          <div className="text-sm space-y-1">
            <p className="text-white/85 inline-flex items-center gap-2"><Calendar size={13} className="text-yellow-400" /> {fmtDateTime(e.event_date)}</p>
            <p className="text-white/75 inline-flex items-start gap-2"><MapPin size={13} className="text-yellow-400 mt-0.5" /> <span>{e.venue}{e.city ? ` · ${e.city}` : ""}</span></p>
            {e.price_ar && <p className="text-yellow-300 font-bold">{fmtAr(e.price_ar)}</p>}
          </div>

          {/* Logo salle + map */}
          <div className="flex items-center gap-3">
            {e.venue_logo_url && (
              <img src={e.venue_logo_url} alt={e.venue} className="h-12 md:h-16 max-w-[100px] object-contain bg-white/5 rounded p-1 border border-white/10" data-testid="venue-logo" />
            )}
            {e.description && <p className="text-xs text-white/65 line-clamp-3 flex-1">{e.description}</p>}
          </div>

          {hasMap && (
            <div className="relative h-44 md:h-56 border border-white/10 overflow-hidden" data-testid="venue-map">
              <iframe src={mapSrc} className="w-full h-full" loading="lazy" title={`Carte ${e.venue}`} />
              <a href={`https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=16/${lat}/${lng}`} target="_blank" rel="noreferrer"
                className="absolute bottom-2 right-2 text-[10px] bg-black/80 backdrop-blur px-2 py-1 uppercase tracking-widest font-bold hover:bg-yellow-400 hover:text-black transition inline-flex items-center gap-1">
                Plein écran <ExternalLink size={10} />
              </a>
            </div>
          )}

          {/* CTA */}
          {e.sold_out ? (
            <span className="self-start px-5 py-3 bg-red-500/20 text-red-300 border border-red-500/40 text-[11px] uppercase tracking-widest font-bold">Complet</span>
          ) : (
            <div className="flex flex-wrap items-center gap-3">
              {e.ticket_url ? (
                <a href={e.ticket_url} target="_blank" rel="noreferrer" className="px-7 py-4 bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-widest text-sm transition inline-flex items-center gap-2">
                  Réserver maintenant <ExternalLink size={14} />
                </a>
              ) : (
                <button onClick={onBook} className="px-7 py-4 bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-widest text-sm transition inline-flex items-center gap-2" data-testid="featured-book-btn">
                  <Ticket size={14} /> Réserver maintenant
                </button>
              )}
              {stripeUrl && (
                <a
                  href={stripeUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="px-6 py-4 bg-white text-black font-black uppercase tracking-widest text-sm transition inline-flex items-center gap-2 hover:bg-yellow-300"
                  data-testid="featured-stripe-btn"
                >
                  <CreditCard size={14} /> Payer avec Stripe
                </a>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const diffParts = (iso) => {
  if (!iso) return { days: 0, hours: 0, mins: 0, secs: 0 };
  const target = new Date(iso.replace(" ", "T")).getTime();
  const ms = Math.max(0, target - Date.now());
  return {
    days:  Math.floor(ms / 86400000),
    hours: Math.floor((ms % 86400000) / 3600000),
    mins:  Math.floor((ms % 3600000) / 60000),
    secs:  Math.floor((ms % 60000) / 1000),
  };
};

const CdBox = ({ value, label }) => (
  <div className="bg-black/50 border border-yellow-400/30 rounded p-2 md:p-3 text-center">
    <p className="font-black text-xl md:text-3xl text-yellow-400 leading-none tabular-nums" style={{ fontFamily: "'Anton', sans-serif" }}>{String(value).padStart(2, "0")}</p>
    <p className="text-[8px] md:text-[9px] uppercase tracking-widest text-white/55 font-bold mt-1">{label}</p>
  </div>
);

// ─── GALLERY PUBLIC — groupée par rubrique avec filtres ───
const GalleryPublic = ({ photos }) => {
  const albums = React.useMemo(() => {
    const set = new Set();
    photos.forEach((p) => { if (p.album) set.add(p.album); });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "fr"));
  }, [photos]);

  const hasOrphans = photos.some((p) => !p.album);
  const tabs = [
    { key: "__all", label: "Toutes" },
    ...albums.map((a) => ({ key: a, label: a })),
    ...(hasOrphans ? [{ key: "__none", label: "Autres" }] : []),
  ];
  const [tab, setTab] = useState("__all");

  const filtered = React.useMemo(() => {
    if (tab === "__all") return photos;
    if (tab === "__none") return photos.filter((p) => !p.album);
    return photos.filter((p) => p.album === tab);
  }, [photos, tab]);

  // Quand on est sur "Toutes", on regroupe par rubrique pour avoir des titres
  const grouped = React.useMemo(() => {
    if (tab !== "__all") return null;
    const map = {};
    photos.forEach((p) => {
      const k = p.album || "__none";
      if (!map[k]) map[k] = [];
      map[k].push(p);
    });
    return map;
  }, [photos, tab]);

  const showFilters = albums.length > 0;

  return (
    <div className="space-y-8">
      {showFilters && (
        <div className="flex flex-wrap items-center gap-2 pb-2">
          {tabs.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-[11px] uppercase tracking-widest font-black transition border ${tab === t.key ? "bg-yellow-400 text-black border-yellow-400" : "bg-transparent text-white/70 border-white/15 hover:border-yellow-400/50 hover:text-yellow-400"}`}
              data-testid={`gallery-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>
      )}

      {grouped ? (
        // Vue "Toutes" : sections par album avec titres
        Object.keys(grouped).sort((a, b) => {
          if (a === "__none") return 1;
          if (b === "__none") return -1;
          return a.localeCompare(b, "fr");
        }).map((alb) => (
          <div key={alb} className="space-y-4">
            <h3 className="font-black text-xl md:text-2xl tracking-tight text-yellow-400 border-b border-yellow-400/30 pb-2" style={{ fontFamily: "'Anton', sans-serif" }}>
              {alb === "__none" ? "AUTRES PHOTOS" : alb.toUpperCase()}
              <span className="text-white/40 text-xs ml-2 font-normal tracking-normal">({grouped[alb].length})</span>
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
              {grouped[alb].map((g) => (
                <a key={g.photo_id} href={g.image_url} target="_blank" rel="noreferrer" className="relative overflow-hidden bg-white/5 aspect-square group" data-testid={`gallery-photo-${g.photo_id}`}>
                  <img src={g.image_url} alt={g.caption || ""} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
                  {g.caption && <span className="absolute bottom-2 left-2 right-2 text-[10px] bg-black/70 backdrop-blur px-2 py-1 uppercase tracking-widest font-bold truncate">{g.caption}</span>}
                </a>
              ))}
            </div>
          </div>
        ))
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
          {filtered.map((g) => (
            <a key={g.photo_id} href={g.image_url} target="_blank" rel="noreferrer" className="relative overflow-hidden bg-white/5 aspect-square group">
              <img src={g.image_url} alt={g.caption || ""} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition duration-500" />
              {g.caption && <span className="absolute bottom-2 left-2 right-2 text-[10px] bg-black/70 backdrop-blur px-2 py-1 uppercase tracking-widest font-bold truncate">{g.caption}</span>}
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── MEMBER CARD — photo + nom + rôle + bio (expandable) ───
const MemberCard = ({ member: m }) => {
  const [open, setOpen] = useState(false);
  const hasBio = !!(m.bio && m.bio.trim());
  return (
    <>
      <div className="group" data-testid={`member-${m.member_id}`}>
        <div
          onClick={() => hasBio && setOpen(true)}
          className={`relative aspect-square bg-white/5 overflow-hidden border border-white/10 group-hover:border-yellow-400/50 transition ${hasBio ? "cursor-pointer" : ""}`}
        >
          {m.photo_url ? (
            <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-white/20 font-black text-4xl">{m.name?.[0]}</div>
          )}
          {hasBio && (
            <div className="hidden md:flex absolute inset-0 bg-black/85 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition duration-300 p-3 flex-col justify-end">
              <p className="text-[11px] text-white/90 leading-snug line-clamp-6">{m.bio}</p>
              <p className="text-[9px] uppercase tracking-widest text-yellow-400 font-bold mt-2">★ Cliquer pour lire</p>
            </div>
          )}
        </div>
        <p className="font-bold mt-2 truncate">{m.name}</p>
        {m.role && <p className="text-[10px] uppercase tracking-widest text-yellow-400/70">{m.role}</p>}
        {hasBio && (
          <p
            onClick={() => setOpen(true)}
            className="md:hidden text-[11px] text-white/65 mt-1.5 leading-snug line-clamp-2 cursor-pointer hover:text-white"
          >
            {m.bio}
          </p>
        )}
      </div>

      {/* Modal bio complète */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid={`member-bio-modal-${m.member_id}`}>
          <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-yellow-400/40 rounded-xl max-w-lg w-full max-h-[90vh] flex flex-col overflow-hidden">
            {m.photo_url && (
              <div className="relative shrink-0 h-[50vh] sm:h-[55vh] bg-black overflow-hidden">
                {/* Fond flouté pour habiller les marges, puis photo complète centrée */}
                <img src={m.photo_url} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-40" />
                <img src={m.photo_url} alt={m.name} className="relative w-full h-full object-contain" />
              </div>
            )}
            <div className="flex items-start justify-between gap-3 px-5 md:px-6 pt-5 shrink-0 border-b border-white/10 pb-4">
              <div>
                <h3 className="font-black text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "'Anton', sans-serif" }}>{m.name}</h3>
                {m.role && <p className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold mt-1">{m.role}</p>}
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/10 text-white/60 hover:text-white transition shrink-0" aria-label="Fermer"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto px-5 md:px-6 py-4 flex-1">
              <p className="text-sm text-white/80 leading-relaxed whitespace-pre-line">{m.bio}</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─── PAST EVENT CARD — concert passé avec sa galerie photos/vidéos ───
const PastEventCard = ({ event: e }) => {
  const d = new Date(e.event_date.replace(" ", "T"));
  const dateLabel = d.toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" });
  const photos = (e.media || []).filter((m) => m.type === "photo");
  const videos = (e.media || []).filter((m) => m.type === "video");
  return (
    <article className="relative border border-white/10 hover:border-orange-400/60 bg-white/[0.02] transition" data-testid={`past-event-${e.event_id}`}>
      <div className="absolute top-3 left-3 z-10 px-3 py-1 bg-orange-500 text-white text-[9px] uppercase tracking-[0.3em] font-black">★ Concert passé</div>
      <div className="grid md:grid-cols-[1fr_1.3fr] gap-0">
        {e.image_url && (
          <a href={e.image_url} target="_blank" rel="noreferrer" className="relative block bg-black aspect-square md:aspect-auto md:max-h-[500px] overflow-hidden">
            <img src={e.image_url} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-40 grayscale" />
            <img src={e.image_url} alt={e.title} className="relative w-full h-full object-contain opacity-95" />
          </a>
        )}
        <div className="p-5 md:p-7 flex flex-col gap-4">
          {e.tour_name && <p className="text-orange-400 text-[10px] uppercase tracking-[0.3em] font-black">{e.tour_name}</p>}
          <h3 className="font-black text-2xl md:text-4xl tracking-tighter leading-[0.95]" style={{ fontFamily: "'Anton', sans-serif" }}>{e.title}</h3>
          <div className="text-sm space-y-1">
            <p className="text-white/85 inline-flex items-center gap-2"><Calendar size={13} className="text-orange-400" /> {dateLabel}</p>
            <p className="text-white/75 inline-flex items-start gap-2"><MapPin size={13} className="text-orange-400 mt-0.5" /> <span>{e.venue}{e.city ? ` · ${e.city}` : ""}</span></p>
          </div>
          {e.description && <p className="text-xs md:text-sm text-white/65 whitespace-pre-line line-clamp-6">{e.description}</p>}
          {(photos.length > 0 || videos.length > 0) && (
            <div className="mt-2 space-y-3">
              {videos.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mb-2">★ Extraits vidéo ({videos.length})</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {videos.map((v) => (
                      <div key={v.media_id} className="aspect-video bg-black border border-white/10">
                        <iframe
                          src={toEmbedUrl(v.url)}
                          title={v.caption || e.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          referrerPolicy="strict-origin-when-cross-origin"
                          loading="lazy"
                          allowFullScreen
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {photos.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-orange-400 font-bold mb-2">★ Photos ({photos.length})</p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                    {photos.map((p) => (
                      <a key={p.media_id} href={p.url} target="_blank" rel="noreferrer" className="block aspect-square overflow-hidden bg-white/5 border border-white/10 hover:border-orange-400/60 transition">
                        <img src={p.url} alt={p.caption || ""} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition duration-500" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

// ─── TOUR DATE ROW ───
const TourDateRow = ({ event: e, onBook }) => {
  const d = new Date(e.event_date.replace(" ", "T"));
  const day = d.toLocaleDateString("fr-FR", { day: "2-digit" });
  const month = d.toLocaleDateString("fr-FR", { month: "short" }).toUpperCase().replace(".", "");
  const year = d.getFullYear();
  return (
    <div className="grid grid-cols-[auto_1fr_auto] md:grid-cols-[auto_auto_1fr_auto] items-center gap-3 md:gap-4 p-3 md:p-5 bg-white/[0.03] hover:bg-white/[0.06] border border-white/5 hover:border-yellow-400/40 transition group" data-testid={`event-${e.event_id}`}>
      <div className="text-center min-w-[64px] md:min-w-[72px] border-r border-white/10 pr-3 md:pr-4">
        <p className="font-black text-2xl md:text-4xl text-yellow-400 leading-none" style={{ fontFamily: "'Anton', sans-serif" }}>{day}</p>
        <p className="text-[10px] uppercase tracking-widest font-bold mt-1">{month}</p>
        <p className="text-[9px] uppercase text-white/45">{year}</p>
      </div>
      {e.image_url && (
        <a href={e.image_url} target="_blank" rel="noreferrer" className="hidden md:block w-20 h-28 lg:w-24 lg:h-32 overflow-hidden bg-white/5 border border-white/10 shrink-0 hover:border-yellow-400/60 transition" data-testid={`event-poster-${e.event_id}`}>
          <img src={e.image_url} alt={e.title} className="w-full h-full object-cover" />
        </a>
      )}
      <div className="min-w-0">
        {e.image_url && (
          <a href={e.image_url} target="_blank" rel="noreferrer" className="md:hidden relative block w-full max-w-[200px] aspect-[3/4] overflow-hidden bg-black border border-white/10 mb-2">
            <img src={e.image_url} alt="" aria-hidden className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl opacity-40" />
            <img src={e.image_url} alt={e.title} className="relative w-full h-full object-contain" />
          </a>
        )}
        <p className="font-black text-base md:text-xl tracking-tight">{e.title}</p>
        <p className="text-sm text-white/55 inline-flex items-center gap-1 mt-0.5"><MapPin size={11} /> {e.venue}{e.city ? ` · ${e.city}` : ""}</p>
        {e.price_ar && <p className="text-yellow-300 font-bold text-sm mt-1">{fmtAr(e.price_ar)}</p>}
      </div>
      {e.sold_out ? (
        <span className="px-3 py-2 bg-red-500/20 text-red-300 border border-red-500/40 text-[10px] uppercase tracking-widest font-bold self-start md:self-center">Complet</span>
      ) : e.ticket_url ? (
        <a href={e.ticket_url} target="_blank" rel="noreferrer" className="px-4 py-2.5 bg-yellow-400 hover:bg-yellow-300 text-black font-black text-[11px] uppercase tracking-widest transition inline-flex items-center gap-1.5 self-start md:self-center" data-testid={`ext-${e.event_id}`}>Billetterie <ExternalLink size={11} /></a>
      ) : (
        <button onClick={onBook} className="px-4 py-2.5 bg-yellow-400 hover:bg-yellow-300 text-black font-black text-[11px] uppercase tracking-widest transition self-start md:self-center" data-testid={`book-${e.event_id}`}>Réserver</button>
      )}
    </div>
  );
};

// ─── PRODUCT CARD — Article boutique ARNAK ───
const ProductCard = ({ product: p, status, stripeUrl }) => {
  const isSoon = status === "soon" || !p.available;
  const canBuy = status === "live" && p.available && !!stripeUrl;
  const price = p.price_ar ? fmtAr(p.price_ar) : (p.price_eur ? `${p.price_eur} €` : null);
  return (
    <article className="group flex flex-col bg-white/[0.03] border border-white/10 hover:border-yellow-400/50 transition" data-testid={`product-${p.product_id}`}>
      <div className="relative aspect-square overflow-hidden bg-black">
        {p.image_url ? (
          <img src={p.image_url} alt={p.name} loading="lazy" className={`w-full h-full object-cover transition duration-700 ${isSoon ? "opacity-60 group-hover:opacity-80" : "group-hover:scale-105"}`} />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-yellow-400/30">
            <ShoppingBag size={50} />
          </div>
        )}
        {isSoon && (
          <div className="absolute inset-0 bg-black/30 flex items-end p-3">
            <span className="px-2.5 py-1 bg-yellow-400 text-black text-[10px] uppercase tracking-widest font-black">★ Bientôt</span>
          </div>
        )}
        {p.category && (
          <span className="absolute top-2.5 left-2.5 px-2 py-0.5 bg-black/80 backdrop-blur text-yellow-400 text-[9px] uppercase tracking-widest font-bold">{p.category}</span>
        )}
      </div>
      <div className="p-4 flex-1 flex flex-col">
        <h3 className="font-bold text-base md:text-lg tracking-tight leading-tight">{p.name}</h3>
        {p.description && <p className="text-xs text-white/60 mt-1 line-clamp-2">{p.description}</p>}
        <div className="mt-auto pt-3 flex items-center justify-between gap-2">
          {price && <p className="text-yellow-300 font-black text-lg" style={{ fontFamily: "'Anton', sans-serif" }}>{price}</p>}
          {canBuy ? (
            <a
              href={stripeUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-widest text-[10px] transition"
              data-testid={`product-buy-${p.product_id}`}
            >
              <CreditCard size={11} /> Acheter
            </a>
          ) : (
            <span className="text-[10px] uppercase tracking-widest font-bold text-yellow-400/70">À venir</span>
          )}
        </div>
      </div>
    </article>
  );
};

// ─── NEWS CARD ───
const NewsCard = ({ item: n }) => {
  const [open, setOpen] = useState(false);
  const media = n.media || [];
  const photos = media.filter((m) => m.type === "photo");
  const videos = media.filter((m) => m.type === "video");
  const cover = n.image_url || photos[0]?.url;
  const hasMore = !!(n.body || photos.length > 1 || videos.length > 0);
  return (
    <>
      <article className="group bg-white/[0.02] hover:bg-white/[0.04] border border-white/10 hover:border-yellow-400/40 transition flex flex-col" data-testid={`news-${n.news_id}`}>
        {cover && (
          <div className="relative aspect-square overflow-hidden bg-white/5 cursor-pointer" onClick={() => hasMore && setOpen(true)}>
            <img src={cover} alt={n.title} className="w-full h-full object-cover group-hover:scale-105 transition duration-700" />
            {(videos.length > 0 || photos.length > 1) && (
              <div className="absolute bottom-2 left-2 flex gap-1.5">
                {videos.length > 0 && (
                  <span className="px-2 py-1 bg-black/85 backdrop-blur text-yellow-300 text-[10px] uppercase tracking-widest font-bold inline-flex items-center gap-1">
                    ▶ {videos.length} vidéo{videos.length > 1 ? "s" : ""}
                  </span>
                )}
                {photos.length > 1 && (
                  <span className="px-2 py-1 bg-black/85 backdrop-blur text-white/90 text-[10px] uppercase tracking-widest font-bold">
                    📷 {photos.length}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
        <div className="p-5 flex-1 flex flex-col">
          {n.published_at && <p className="text-yellow-400 text-[10px] uppercase tracking-widest font-bold mb-2">{fmtDate(n.published_at)}</p>}
          <h3 className="font-black text-xl tracking-tight mb-2 leading-tight">{n.title}</h3>
          {n.excerpt && <p className="text-sm text-white/65 mb-3 line-clamp-3">{n.excerpt}</p>}
          <div className="mt-auto flex flex-wrap items-center gap-3">
            {hasMore && (
              <button onClick={() => setOpen(true)} className="text-yellow-400 text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1 hover:text-yellow-300 transition" data-testid={`news-open-${n.news_id}`}>
                Voir plus <ChevronRight size={12} />
              </button>
            )}
            {n.link_url && (
              <a href={n.link_url} target="_blank" rel="noreferrer" className="text-white/55 text-xs uppercase tracking-widest font-bold inline-flex items-center gap-1 hover:text-yellow-300 transition">
                Lien externe <ExternalLink size={11} />
              </a>
            )}
          </div>
        </div>
      </article>

      {/* Modal — lecture vidéo + galerie photos + texte */}
      {open && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setOpen(false)} data-testid={`news-modal-${n.news_id}`}>
          <div onClick={(e) => e.stopPropagation()} className="bg-zinc-950 border border-yellow-400/30 rounded-xl max-w-3xl w-full max-h-[92vh] flex flex-col overflow-hidden">
            <div className="flex items-start justify-between gap-3 px-5 md:px-6 py-4 border-b border-white/10 shrink-0">
              <div className="min-w-0">
                {n.published_at && <p className="text-yellow-400 text-[10px] uppercase tracking-widest font-bold mb-1">{fmtDate(n.published_at)}</p>}
                <h3 className="font-black text-2xl md:text-3xl tracking-tight leading-tight truncate" style={{ fontFamily: "'Anton', sans-serif" }}>{n.title}</h3>
              </div>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-full hover:bg-white/10 text-white/60 hover:text-white transition shrink-0" aria-label="Fermer"><X size={18} /></button>
            </div>
            <div className="overflow-y-auto flex-1 px-5 md:px-6 py-5 space-y-5">
              {videos.length > 0 && (
                <div className="space-y-3">
                  {videos.map((v) => (
                    <div key={v.media_id}>
                      <div className="aspect-video bg-black border border-white/10">
                        <iframe
                          src={toEmbedUrl(v.url)}
                          title={v.caption || n.title}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                          referrerPolicy="strict-origin-when-cross-origin"
                          loading="lazy"
                          allowFullScreen
                        />
                      </div>
                      {v.caption && <p className="text-xs text-white/55 italic mt-1.5">{v.caption}</p>}
                    </div>
                  ))}
                </div>
              )}

              {(n.excerpt || n.body) && (
                <div className="space-y-2 text-white/85 text-sm md:text-base leading-relaxed">
                  {n.excerpt && <p className="text-base md:text-lg font-bold text-white">{n.excerpt}</p>}
                  {n.body && <p className="whitespace-pre-line">{n.body}</p>}
                </div>
              )}

              {photos.length > 0 && (
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-yellow-400 font-bold mb-3">★ Photos ({photos.length})</p>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {photos.map((p) => (
                      <a key={p.media_id} href={p.url} target="_blank" rel="noreferrer" className="block aspect-square overflow-hidden bg-white/5 border border-white/10 hover:border-yellow-400/60 transition">
                        <img src={p.url} alt={p.caption || ""} loading="lazy" className="w-full h-full object-cover hover:scale-105 transition duration-500" />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {n.link_url && (
                <a href={n.link_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-yellow-400 text-xs uppercase tracking-widest font-bold hover:text-yellow-300 transition">
                  Lien externe <ExternalLink size={12} />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

// ─── NEWSLETTER ───
const NewsletterBlock = ({ user }) => {
  const [email, setEmail] = useState(user?.email || "");
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (user?.email) setEmail(user.email); }, [user]);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/arnak/newsletter", { email });
      setDone(true);
      toast.success("Inscription confirmée ✨");
    } catch { toast.error("Erreur d'inscription"); }
    finally { setLoading(false); }
  };
  return (
    <section className="px-6 md:px-12 py-16 md:py-20 max-w-3xl mx-auto text-center">
      <Newspaper size={32} className="mx-auto text-yellow-400 mb-4" />
      <h2 className="font-black text-3xl md:text-5xl tracking-tighter mb-3" style={{ fontFamily: "'Anton', sans-serif" }}>NEWSLETTER</h2>
      <p className="text-white/65 text-sm md:text-base mb-6">Reçois en avant-première les nouvelles dates, sorties et coulisses du collectif.</p>
      {done ? (
        <p className="text-yellow-300 font-bold inline-flex items-center gap-2"><Flame size={14} /> C'est noté · à très vite</p>
      ) : (
        <form onSubmit={submit} className="flex flex-col sm:flex-row items-stretch gap-2 max-w-md mx-auto" data-testid="newsletter-form">
          <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="ton@email.com" className="flex-1 bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-yellow-400" data-testid="newsletter-email" />
          <button type="submit" disabled={loading} className="bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-widest text-xs px-6 py-3 disabled:opacity-50" data-testid="newsletter-submit">
            {loading ? "…" : "S'abonner"}
          </button>
        </form>
      )}
      <p className="text-[10px] text-white/35 mt-4 max-w-md mx-auto">En t'inscrivant tu acceptes de recevoir des emails. Désinscription possible à tout moment.</p>
    </section>
  );
};

// ─── LOGIN MODAL (utilise auth Voamada existante) ───
const LoginModal = ({ onClose, onSuccess }) => {
  const [mode, setMode] = useState("login"); // login|register
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const url = mode === "login" ? "/auth/login" : "/auth/register";
      const { data } = await api.post(url, form);
      if (data.token) setToken(data.token);
      const me = await api.get("/auth/me");
      onSuccess(me.data);
      toast.success(mode === "login" ? "Connecté" : "Compte créé ✨");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    } finally { setLoading(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-950 border-2 border-yellow-400 max-w-md w-full p-6 md:p-8 relative" onClick={(e) => e.stopPropagation()} data-testid="arnak-login-modal">
        <button onClick={onClose} className="absolute top-3 right-3 text-white/50 hover:text-white"><X size={20} /></button>
        <h3 className="font-black text-2xl mb-1 tracking-tight">{mode === "login" ? "Connexion" : "Créer un compte"}</h3>
        <p className="text-white/55 text-xs mb-5">Ton compte Voamada te permet aussi d'accéder à ARNAK.</p>
        <form onSubmit={submit} className="space-y-3">
          {mode === "register" && (
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nom complet" className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-yellow-400" />
          )}
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-yellow-400" data-testid="arnak-login-email" />
          <input required type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="Mot de passe" className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-yellow-400" data-testid="arnak-login-password" />
          <button type="submit" disabled={loading} className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-widest text-sm py-3 disabled:opacity-50" data-testid="arnak-login-submit">
            {loading ? "…" : (mode === "login" ? "Se connecter" : "Créer le compte")}
          </button>
        </form>
        <button onClick={() => setMode(mode === "login" ? "register" : "login")} className="block w-full text-center mt-4 text-xs text-white/55 hover:text-yellow-400 transition">
          {mode === "login" ? "Pas encore de compte ? Inscris-toi" : "Déjà inscrit ? Connecte-toi"}
        </button>
      </div>
    </div>
  );
};

// ─── ACCOUNT MODAL ───
const AccountModal = ({ user, onClose, onLogout }) => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/me/arnak/tickets").then(({ data }) => setTickets(data || [])).finally(() => setLoading(false));
  }, []);
  return (
    <div className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-zinc-950 border-2 border-yellow-400 max-w-2xl w-full p-6 md:p-8 relative max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="arnak-account-modal">
        <button onClick={onClose} className="absolute top-3 right-3 text-white/50 hover:text-white"><X size={20} /></button>
        <div className="flex items-center gap-3 mb-6">
          {user.picture ? <img src={user.picture} alt="" className="w-12 h-12 rounded-full object-cover" /> : <div className="w-12 h-12 rounded-full bg-yellow-400/20 flex items-center justify-center font-black text-yellow-300">{user.name?.[0]}</div>}
          <div className="flex-1">
            <p className="font-black text-lg tracking-tight">{user.name}</p>
            <p className="text-white/55 text-xs">{user.email}</p>
          </div>
          <button onClick={onLogout} className="text-xs text-white/55 hover:text-red-400 transition uppercase tracking-widest font-bold">Déconnexion</button>
        </div>

        <h4 className="font-black text-lg tracking-tight mb-3 inline-flex items-center gap-2"><Ticket size={16} className="text-yellow-400" /> Mes billets</h4>
        {loading ? <p className="text-white/55 text-sm">Chargement…</p>
         : tickets.length === 0 ? <p className="text-white/55 text-sm py-4 text-center bg-white/[0.02] border border-dashed border-white/10">Aucune réservation pour le moment.</p>
         : (
          <div className="space-y-2">
            {tickets.map((t) => (
              <div key={t.ticket_id} className="p-3 rounded bg-white/[0.03] border border-white/10 flex items-center gap-3">
                {t.image_url && <img src={t.image_url} alt="" className="w-12 h-12 object-cover rounded shrink-0" />}
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-sm truncate">{t.event_title}</p>
                  <p className="text-xs text-white/55 truncate">{fmtDateTime(t.event_date)} · {t.venue} · {t.city}</p>
                  <p className="text-xs text-yellow-300 font-bold">×{t.qty}{t.total_ar ? ` · ${fmtAr(t.total_ar)}` : ""}</p>
                </div>
                <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-full ${
                  t.status === "paid" ? "bg-emerald-500/20 text-emerald-300" :
                  t.status === "confirmed" ? "bg-blue-500/20 text-blue-300" :
                  t.status === "cancelled" ? "bg-red-500/20 text-red-300" :
                  "bg-yellow-400/20 text-yellow-300"
                }`}>{t.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ─── BOOKING MODAL ───
const BookingModal = ({ event, onClose, user }) => {
  const [form, setForm] = useState({ buyer_name: user?.name || "", buyer_phone: "", buyer_email: user?.email || "", qty: 1, note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const total = (event.price_ar || 0) * (form.qty || 1);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.buyer_name || !form.buyer_phone) { toast.error("Nom et téléphone requis"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/arnak/tickets", { event_id: event.event_id, ...form });
      setDone(data);
      toast.success("Réservation envoyée ✨");
    } catch (err) { toast.error(err?.response?.data?.error || "Erreur"); }
    finally { setSubmitting(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-zinc-950 border-2 border-yellow-400 max-w-lg w-full relative my-4" onClick={(e) => e.stopPropagation()} data-testid="booking-modal">
        <button onClick={onClose} className="absolute top-3 right-3 z-20 p-1.5 rounded-full bg-black/70 text-white/80 hover:text-white hover:bg-black"><X size={18} /></button>
        {done ? (
          <div className="text-center py-10 px-6" data-testid="booking-success">
            <div className="w-16 h-16 mx-auto rounded-full bg-yellow-400/20 flex items-center justify-center mb-4"><Ticket size={28} className="text-yellow-400" /></div>
            <h3 className="font-black text-2xl mb-2">Réservation reçue</h3>
            <p className="text-white/70 text-sm mb-1">Réf. <span className="font-mono text-yellow-300">{done.ticket_id}</span></p>
            <p className="text-white/55 text-xs mb-6">Nous te recontactons sous 24h.</p>
            <button onClick={onClose} className="bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-widest text-xs px-6 py-3">Fermer</button>
          </div>
        ) : (
          <>
            {/* Affiche du concert en grand */}
            {event.image_url && (
              <a href={event.image_url} target="_blank" rel="noreferrer" className="block w-full bg-black">
                <img src={event.image_url} alt={event.title} className="w-full h-auto max-h-[55vh] object-contain" data-testid="booking-poster" />
              </a>
            )}
            <div className="p-5 md:p-7">
              <p className="text-yellow-400 text-[10px] uppercase tracking-[0.3em] font-bold mb-1">{fmtDateTime(event.event_date)}</p>
              <h3 className="font-black text-2xl tracking-tight mb-1" style={{ fontFamily: "'Anton', sans-serif" }}>{event.title}</h3>
              <p className="text-white/55 text-sm mb-5 inline-flex items-center gap-1"><MapPin size={12} /> {event.venue}{event.city ? ` · ${event.city}` : ""}</p>
              <form onSubmit={submit} className="space-y-3">
                <input required value={form.buyer_name} onChange={(e) => setForm({ ...form, buyer_name: e.target.value })} placeholder="Nom complet" className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-yellow-400" data-testid="bk-name" />
                <input required value={form.buyer_phone} onChange={(e) => setForm({ ...form, buyer_phone: e.target.value })} placeholder="Téléphone" className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-yellow-400" data-testid="bk-phone" />
                <input type="email" value={form.buyer_email} onChange={(e) => setForm({ ...form, buyer_email: e.target.value })} placeholder="Email" className="w-full bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-yellow-400" data-testid="bk-email" />
                <div className="grid grid-cols-[1fr_auto] gap-3 items-center">
                  <input type="number" min="1" max="20" value={form.qty} onChange={(e) => setForm({ ...form, qty: +e.target.value })} placeholder="Places" className="bg-white/5 border border-white/10 px-4 py-3 text-sm focus:outline-none focus:border-yellow-400" data-testid="bk-qty" />
                  {event.price_ar > 0 && <span className="text-yellow-300 font-black text-xl tabular-nums">{fmtAr(total)}</span>}
                </div>
                <button type="submit" disabled={submitting} className="w-full bg-yellow-400 hover:bg-yellow-300 text-black font-black uppercase tracking-widest text-sm py-4 disabled:opacity-50" data-testid="bk-submit">
                  {submitting ? "Envoi…" : "Confirmer"}
                </button>
              </form>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ArnakPage;
